<template>
    <div class="inline-block">
        <button
            ref="triggerRef"
            type="button"
            class="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 text-gray-500 hover:text-gray-800 hover:border-gray-400 transition-colors"
            :aria-label="label"
            :aria-expanded="open ? 'true' : 'false'"
            aria-haspopup="menu"
            :title="label"
            @click.stop="toggle"
        >
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 12a2 2 0 11-4 0 2 2 0 014 0zm8 0a2 2 0 11-4 0 2 2 0 014 0zm8 0a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
        </button>

        <!-- Teleported and fixed-positioned on purpose: this menu lives inside the
             documents table, whose wrapper is overflow-x-auto. When one overflow
             axis is auto the other computes to auto too, so an absolutely
             positioned menu (e.g. Jetstream's Dropdown) is clipped on the lower
             rows instead of overflowing the table. -->
        <Teleport to="body">
            <div v-if="open" class="fixed inset-0 z-40" @click="close"></div>
            <div
                v-if="open"
                ref="menuRef"
                role="menu"
                class="fixed z-50 w-52 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5"
                :style="{ top: `${position.top}px`, left: `${position.left}px` }"
                @click="close"
            >
                <slot />
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import { nextTick, onUnmounted, ref } from 'vue'

defineProps({
    label: {
        type: String,
        default: 'More actions',
    },
})

const MENU_WIDTH = 208
const GAP = 4

const open = ref(false)
const triggerRef = ref(null)
const menuRef = ref(null)
const position = ref({ top: 0, left: 0 })

// Measured against the viewport, then flipped upward when the menu would run off
// the bottom — the same reason it is teleported in the first place.
const place = () => {
    const trigger = triggerRef.value?.getBoundingClientRect()
    if (!trigger) {
        return
    }

    const height = menuRef.value?.offsetHeight || 0
    const flipUp = height > 0 && trigger.bottom + GAP + height > window.innerHeight

    position.value = {
        top: flipUp ? Math.max(GAP, trigger.top - GAP - height) : trigger.bottom + GAP,
        left: Math.max(GAP, Math.min(trigger.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - GAP)),
    }
}

const close = () => {
    open.value = false
    window.removeEventListener('resize', close)
    window.removeEventListener('scroll', close, true)
    document.removeEventListener('keydown', closeOnEscape)
}

const closeOnEscape = (event) => {
    if (event.key === 'Escape') {
        close()
    }
}

const toggle = async () => {
    if (open.value) {
        close()
        return
    }

    open.value = true
    place()
    // Re-place once rendered: the flip-up branch needs the real height.
    await nextTick()
    place()

    window.addEventListener('resize', close)
    // Capture phase so scrolling ANY ancestor (including the table's own
    // horizontal scroller) dismisses it rather than leaving it stranded.
    window.addEventListener('scroll', close, true)
    document.addEventListener('keydown', closeOnEscape)
}

onUnmounted(close)
</script>
